import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IPermissionContextState from 'Models/PermissionContextModel'
import WithUserContext from 'Hoc/WithUserContext'
import InsuranceFieldListItem from './InsuranceFieldListItem'
import LoadingPage from '../../../Layouts/LoadingPage/LoadingPage'
import { useNavigate } from 'react-router-dom'
import Signature from 'Components/Signature'
import { instance } from 'Services/instance'
import Const from 'Utils/Consts'
import VisitsApiPaths from 'Application/Visits/Constants/VisitsApiPaths'
import Icon from 'Components/Icon'
import ErrorListItem from './ErrorListItem'
import { useGetUserProfileQuery } from 'Services/api'
import { useUploadSignatureFile } from '../hooks'

type IProps = IPermissionContextState

const Account: FC<IProps> = () => {
  const [signatureImage, setSignatureImage] = useState<string>('')

  const [t] = useTranslation()
  const navigate = useNavigate()

  const { data: userProfileData, isLoading: isUserLoading } = useGetUserProfileQuery()
  const { uploadSignatureFile, isUploadLoading } = useUploadSignatureFile()

  const getImageStyle = (id: number) => {
    if (id) {
      if (signatureImage) return { backgroundImage: `url(${signatureImage})` }
      else return { backgroundImage: `url(${getImage(id)})` }
    }
    return {}
  }

  const getImage = async (fileId: number) => {
    await instance
      .get(
        `${Const.env.REACT_APP_API_GATEWAY_URL}${VisitsApiPaths.DownloadThumbnailFile}${fileId}`,
        { timeout: 60000, responseType: 'arraybuffer' }
      )
      .then((res) => {
        if (res.status === 200) {
          let binary = ''
          const bytes = new Uint8Array(res.data)
          for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
          const base64String = window.btoa(binary)
          setSignatureImage(`data:${res.headers['content-type']};base64,${base64String}`)
          return `data:${res.headers['content-type']};base64,${base64String}`
        }
      })
      .catch((error) => {})
  }

  return (
    <>
      <div className="flex justify-between items-center h-16 px-6 shrink-0">
        <div className="flex justify-between items-center gap-2">
          <div className="text-lg font-bold pl-2">{t('ACCOUNT')}</div>
        </div>
        <div className="flex justify-between items-center" onClick={() => navigate('/SignOut')}>
          <button className="btn btn-text-error flex gap-2 items-center">
            <Icon name="logout" className="w-5 h-5" />
            {t('LOGOUT')}
          </button>
        </div>
      </div>
      <div className="flex flex-col grow overflow-auto">
        {isUserLoading ? (
          <LoadingPage />
        ) : (
          userProfileData && (
            <div className="space-y-4">
              <div>
                <div className="flex gap-2 px-6 py-3">
                  <div className="avatar avatar-circular avatar-secondary avatar-48">
                    <Icon name="profile" />
                  </div>
                  <div className="flex flex-col gap-2 grow overflow-hidden">
                    <div className="font-semibold text-sm truncate text-gray-800">
                      {t('EXPERT_FULL_NAME')}
                    </div>
                    <div className="text-xs text-gray-500 shrink-0">{userProfileData.fullName}</div>
                  </div>
                </div>

                <div className="flex gap-2 px-6 py-3">
                  <div className="avatar avatar-circular avatar-secondary avatar-48">
                    <Icon name="call" />
                  </div>
                  <div className="flex flex-col gap-2 grow overflow-hidden">
                    <div className="font-semibold text-sm truncate text-gray-800">
                      {t('EXPERT_MOBILE_PHONE_NUMBER')}
                    </div>
                    <div className="text-xs text-gray-500 shrink-0">
                      {userProfileData.phoneNumber}
                    </div>
                  </div>
                </div>

                {/* <div className="flex gap-2 px-6 py-3">
                                <div className="avatar avatar-circular avatar-secondary avatar-48">
                                    <Icon name="sms" />
                                </div>
                                <div className="flex flex-col gap-2 grow overflow-hidden">
                                    <div className="font-semibold text-sm truncate text-gray-800">
                                        {t("EMAIL")}
                                    </div>
                                    <div className="text-xs text-gray-500 shrink-0">{userProfileData.email}</div>
                                </div>
                            </div> */}

                <div className="flex gap-2 px-6 py-3">
                  <div className="avatar avatar-circular avatar-secondary avatar-48">
                    <Icon name="building-4" />
                  </div>
                  <div className="flex flex-col gap-2 grow overflow-hidden">
                    <div className="font-semibold text-sm truncate text-gray-800">
                      {t('EXPERT_UNIT')}
                    </div>
                    <div className="text-xs text-gray-500 shrink-0">
                      {userProfileData.expertUnitTitle}
                    </div>
                  </div>
                </div>

                {/* <div className="flex gap-2 px-6 py-3">
                        <div className="avatar avatar-circular avatar-primary avatar-48">
                        <svg>
                        <use href="#profile"></use>
                        </svg>
                        </div>
                        <div className="flex flex-col gap-2 grow overflow-hidden">
                        <div className="font-semibold text-sm truncate text-gray-800">
                        کارشناس مجاز
                        </div>
                        <div className="text-xs text-gray-500 shrink-0">صدور</div>
                        </div>
                    </div> */}
              </div>

              <div className="font-bold text-base px-6">{t('ACCESSES')}</div>
              <div>
                {userProfileData.insuranceFieldList?.map((item: any, index: number) => {
                  return <InsuranceFieldListItem {...item} key={index} />
                })}
              </div>

              {userProfileData.errorMessages && userProfileData.errorMessages.length ? (
                <>
                  <div className="font-bold text-base px-6">{t('ERRORS')}</div>
                  <div>
                    {userProfileData.errorMessages?.map((item: any, index: number) => {
                      return <ErrorListItem message={item} key={index} />
                    })}
                  </div>
                </>
              ) : null}

              <div className="font-bold text-base px-6">{t('SIGNATURE')}</div>
              {userProfileData.signatureImageId ? (
                <div className="flex justify-center items-center">
                  <div
                    className="flex flex-col justify-between w-40 h-36 rounded-lg border border-dashed border-gray-500 bg-cover bg-no-repeat bg-center overflow-hidden"
                    style={getImageStyle(userProfileData.signatureImageId)}
                  ></div>
                </div>
              ) : (
                <>
                  {userProfileData?.expertId && (
                    <Signature
                      title={t('SIGNATURE')}
                      loading={isUploadLoading}
                      onCapture={(image, fileType, onComplete) => {
                        uploadSignatureFile({
                          file: { image, fileType },
                          expertId: userProfileData.expertId,
                          onComplete,
                        })
                      }}
                    />
                  )}
                  <div className="px-6">
                    <div
                      id="alert-info"
                      className="flex items-center p-3 text-white rounded-xl bg-gray-800 dark:bg-gray-800"
                      role="alert"
                    >
                      <div className="bg-info/[.16] text-info p-2 rounded-lg flex justify-center items-center">
                        <Icon name="ic_info" className="w-6 h-6" />
                      </div>
                      <span className="sr-only">info</span>
                      <div className="ms-3 text-sm font-semibold grow">
                        {t('SIGNATURE_REQUIRED_MESSAGE')}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        )}
      </div>
    </>
  )
}

export default WithUserContext(Account)
